import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      if (sent) toast.success('Sent again. Check your inbox and spam folder.');
      setSent(true);
      setCooldown(30);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center p-8">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center text-stone-600 hover:text-stone-900 mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Sign In
        </Link>
        <Card className="border-stone-200 shadow-xl" data-testid="forgot-password-card">
          <CardHeader>
            <CardTitle className="text-2xl font-heading">Forgot your password?</CardTitle>
            <CardDescription>Enter your email and we'll send you a link to choose a new one.</CardDescription>
          </CardHeader>
          <CardContent>
            {sent ? (
              <div className="space-y-5">
                <p className="text-stone-700" data-testid="forgot-password-sent">
                  If <b>{email}</b> has an account, a reset link is on its way. It works for 1 hour. Check your spam folder too.
                </p>
                <Button
                  type="button"
                  onClick={() => handleSubmit()}
                  disabled={loading || cooldown > 0}
                  className="w-full h-11 rounded-full bg-stone-900 text-white hover:bg-stone-800 font-medium"
                  data-testid="forgot-password-resend-btn"
                >
                  {loading ? 'Sending...' : cooldown > 0 ? `Didn't get it? Send again in ${cooldown}s` : "Didn't get it? Send again"}
                </Button>
                <button
                  type="button"
                  onClick={() => { setSent(false); setCooldown(0); }}
                  className="w-full text-sm text-stone-600 hover:text-stone-900"
                  data-testid="forgot-password-change-email-btn"
                >
                  Use a different email
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-stone-700">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-11 rounded-lg border-stone-200 focus:border-[#D97757] focus:ring-[#D97757]"
                    data-testid="forgot-password-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 rounded-full bg-stone-900 text-white hover:bg-stone-800 font-medium"
                  disabled={loading}
                  data-testid="forgot-password-submit-btn"
                >
                  {loading ? 'Sending...' : 'Send reset link'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
